import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import CTASection from "@/components/feature/CTASection";

const SERVICES = [
  { icon: "ri-search-eye-line", title: "제품 선정", desc: "장비 사양에 맞는 서보밸브·모터·센서 선정" },
  { icon: "ri-draft-line", title: "회로 검토", desc: "유압·전기 회로도 검토 및 보완" },
  { icon: "ri-database-2-line", title: "유압 시스템 구성", desc: "HPU, 매니폴드, 실린더 통합 구성" },
  { icon: "ri-flow-chart", title: "전기 배선 검토", desc: "결선도 작성 및 현장 배선 검토" },
  { icon: "ri-cpu-line", title: "I/O 매핑 검토", desc: "Analog/Digital I/O 매핑 및 신호 흐름 검증" },
  { icon: "ri-equalizer-line", title: "Signal Test", desc: "Analog / Digital Signal 점검" },
  { icon: "ri-mic-line", title: "Servo Valve Command Test", desc: "Command 신호 및 응답성 테스트" },
  { icon: "ri-ruler-2-line", title: "SSI Position Sensor Setting", desc: "SSI 위치센서 셋업 및 스케일링" },
  { icon: "ri-pulse-line", title: "Pressure Sensor Scaling", desc: "압력센서 캘리브레이션 및 스케일링" },
  { icon: "ri-loop-right-line", title: "PID Control Support", desc: "PID 게인 튜닝 및 안정화 지원" },
  { icon: "ri-tools-line", title: "Site Commissioning", desc: "현장 시운전 및 라인 안정화" },
  { icon: "ri-error-warning-line", title: "Trouble Shooting", desc: "현장 트러블 슈팅 대응" },
  { icon: "ri-shield-check-line", title: "Repair & Maintenance", desc: "수리·유지보수 지원" },
];

const KEYWORDS = [
  "Closed-loop Position Control",
  "Pressure Control",
  "Roll Gap Control",
  "Servo Hydraulic Control",
  "Motion Control",
  "Analog 4~20mA / ±10V",
  "SSI Feedback",
  "PID Control",
  "EtherCAT",
  "PLC Interface",
];

export default function EngineeringPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Engineering"
        title="Engineering & Technical Support"
        subtitle="제품 공급뿐 아니라 회로 검토, I/O 구성, 제어 프로그램, 현장 시운전까지 지원하는 엔지니어링 서비스"
        crumbs={[{ label: "HOME", to: "/" }, { label: "Engineering" }]}
        imageUrl="https://readdy.ai/api/search-image?query=Industrial%20engineer%20working%20on%20servo%20hydraulic%20control%20cabinet%20with%20laptop%20and%20PLC%20programming%2C%20clean%20modern%20factory%20background%2C%20cool%20industrial%20lighting%2C%20detailed%20mechanical%20textures%2C%20editorial%20B2B%20engineering%20photography%2C%20muted%20steel%20gray%20and%20cool%20blue%20palette&width=1920&height=900&seq=mctech-engineering-hero-01&orientation=landscape"
      />

      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <p className="text-base md:text-lg text-foreground-800 leading-relaxed">
            MC TECH는 제품 공급뿐만 아니라 장비에 맞는 유압 회로, 전기 배선,
            I/O 구성, 제어 프로그램, 파라미터 설정, 현장 시운전까지 지원합니다.
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="mb-10 text-center">
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase text-accent-700 mb-3">
              <span className="w-6 h-px bg-accent-500" />
              Key Services
              <span className="w-6 h-px bg-accent-500" />
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-foreground-950">
              주요 서비스
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((s, i) => (
              <div
                key={s.title}
                className="bg-background-50 border border-background-200 rounded-xl p-5 hover:border-primary-300 transition-colors"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                    <i className={`${s.icon} text-lg w-5 h-5 flex items-center justify-center`} />
                  </div>
                  <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-secondary-600">
                    Service {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="text-base font-heading font-bold text-foreground-950">
                  {s.title}
                </h3>
                <p className="mt-1.5 text-sm text-foreground-700 leading-relaxed">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Keywords */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="bg-background-100 border border-background-200 rounded-2xl p-6 md:p-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
              <div>
                <span className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase text-accent-700 mb-2">
                  <span className="w-6 h-px bg-accent-500" />
                  Technical Keywords
                </span>
                <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-foreground-950">
                  대응 가능한 기술 키워드
                </h2>
              </div>
              <div className="text-sm text-foreground-700">
                실제 현장에서 자주 다루는 제어 기술 영역입니다.
              </div>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {KEYWORDS.map((k) => (
                <span
                  key={k}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-50 border border-background-300 text-sm font-semibold text-foreground-900 whitespace-nowrap"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                  {k}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection
        title="기술 검토가 필요하신가요?"
        description="회로도, 도면, 기존 장비 정보를 함께 보내주시면 정확한 검토와 솔루션을 제안드립니다."
      />
    </SiteLayout>
  );
}